import {Component, EventEmitter, HostListener, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {takeUntil} from 'rxjs/operators';
import {EmailService} from '../email.service';
import {Observable, Subject} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {NgForm} from '@angular/forms';
import {NgbModal, NgbModalOptions, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
//import {DocumentService} from '../../../../../services/document/document.service';
import {DocumentService} from '../../../../../../services/document/document.service';
import {ClientService} from '../../../../../../services/Client/client.service';
import {FournisseurService} from '../../../../../../services/Fournisseur/fournisseur.service';

@Component({
    selector: 'app-email-compose',
    templateUrl: './email-compose.component.html',
    styleUrls: ['./email-compose.component.scss'],
})
export class EmailComposeComponent implements OnInit {
    @Input() reference: string = "DEV-000001";
    @Input() date: string = "2025-04-15";
    @Input() total: string = "300,600 TND";
    @Input() documentId: number | null = null; // ID du document pour l'envoi d'email
    @Input() clientId: number | null = null; // ID du client pour l'envoi d'email
    @Input() fournisseurId: number | null = null; // ID du fournisseur pour l'envoi d'email
    @Output() closed = new EventEmitter<void>();

    email = {
        to: '',
        cc: '',
        subject: '',
        body: ''
    };

    close() {
        this.closed.emit();
    }

    sendEmail() {
        // Vérifier que l'email destinataire est rempli
        if (!this.email.to || this.email.to.trim() === '') {
            this.toastr.error('Veuillez saisir un destinataire (à)', 'Erreur');
            return;
        }

        // Vérifier qu'au moins un ID est présent (documentId, clientId ou fournisseurId)
        if (!this.documentId && !this.clientId && !this.fournisseurId) {
            this.toastr.error('ID manquant. Impossible d\'envoyer l\'email.', 'Erreur');
            return;
        }

        // Récupérer user_id depuis localStorage
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const userId = currentUser?.id;

        if (!userId) {
            this.toastr.error('Utilisateur non trouvé. Veuillez vous reconnecter.', 'Erreur');
            return;
        }

        // Construire le payload selon le format de l'API
        // Toujours extraire le texte brut du HTML pour supprimer les styles (italique, gras, souligné)
        let messageBody = '';
        if (this.email.body && this.email.body.trim() !== '') {
            // Toujours utiliser stripHtmlTags pour supprimer toutes les balises HTML (i, b, u, strong, em, etc.)
            const originalBody = this.email.body;
            messageBody = this.stripHtmlTags(this.email.body);
            console.log('📧 [EmailComposeComponent] Body original (avec HTML):', originalBody);
            console.log('📧 [EmailComposeComponent] Body nettoyé (texte brut):', messageBody);
        } else {
            messageBody = `Cher, Merci pour votre confiance. Votre document peut être visualisé, imprimé et téléchargé au format PDF à partir de la pièce jointe. Référence : ${this.reference} Date : ${this.date} Total : ${this.total}`;
        }

        const emailPayload = {
            to: this.email.to.trim(),
            user_id: userId,
            cc: this.email.cc && this.email.cc.trim() !== '' ? this.email.cc.trim() : undefined,
            subject: this.emailData.subject && this.emailData.subject.trim() !== '' 
                ? this.emailData.subject.trim() 
                : 'Votre document',
            message: messageBody
        };

        // Supprimer cc s'il est vide
        if (!emailPayload.cc) {
            delete emailPayload.cc;
        }

        // Déterminer quel service utiliser selon l'ID disponible
        let emailObservable: Observable<any>;

        if (this.documentId) {
            console.log('📧 [EmailComposeComponent.sendEmail] Envoi email pour document:', this.documentId);
            emailObservable = this.documentService.sendEmail(this.documentId, emailPayload);
        } else if (this.clientId) {
            console.log('📧 [EmailComposeComponent.sendEmail] Envoi email pour client:', this.clientId);
            emailObservable = this.clientService.sendEmail(this.clientId, emailPayload);
        } else if (this.fournisseurId) {
            console.log('📧 [EmailComposeComponent.sendEmail] Envoi email pour fournisseur:', this.fournisseurId);
            emailObservable = this.fournisseurService.sendEmail(this.fournisseurId, emailPayload);
        } else {
            this.toastr.error('Aucun ID valide trouvé pour l\'envoi de l\'email.', 'Erreur');
            return;
        }

        console.log('📧 [EmailComposeComponent.sendEmail] Payload:', emailPayload);

        // Appeler l'API pour envoyer l'email
        emailObservable.subscribe({
            next: (response) => {
                console.log('✅ [EmailComposeComponent.sendEmail] Email envoyé avec succès:', response);
                this.toastr.success('Email envoyé avec succès', 'Succès');
                this.closeCompose();
                if (this.modalRef) {
                    this.modalRef.close();
                }
            },
            error: (error) => {
                console.error('❌ [EmailComposeComponent.sendEmail] Erreur lors de l\'envoi:', error);
                const errorMessage = error?.error?.message || error?.message || 'Erreur lors de l\'envoi de l\'email';
                this.toastr.error(errorMessage, 'Erreur');
            }
        });
    }

    format(command: string) {
        document.execCommand(command, false, '');
    }

    onContentChange(content: string) {
        this.email.body = content;
    }

    /**
     * Extraire le texte brut du HTML en supprimant toutes les balises HTML
     * Supprime les styles (italique <i>, gras <b>, souligné <u>, etc.)
     * @param html Le contenu HTML
     * @returns Le texte brut sans balises HTML
     */
    private stripHtmlTags(html: string): string {
        if (!html) return '';
        
        // Créer un élément temporaire pour extraire le texte
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        
        // Récupérer le texte brut (supprime automatiquement toutes les balises HTML)
        let text = tmp.textContent || tmp.innerText || '';
        
        // Nettoyer les espaces multiples, les sauts de ligne et les caractères spéciaux
        text = text
            .replace(/\s+/g, ' ')  // Remplacer les espaces multiples par un seul espace
            .replace(/\n+/g, ' ')  // Remplacer les sauts de ligne par un espace
            .replace(/\r+/g, ' ')  // Remplacer les retours chariot par un espace
            .trim();
        
        return text;
    }
    @ViewChild('modalLG') modalTemplate!: TemplateRef<any>;

    @HostListener('keydown.escape') fn() {
        this.closeCompose();
    }

    @ViewChild('selectRef') private _selectRef: any;
    @ViewChild('emailForm') emailForm!: NgForm;

    public emailToSelect = [
        {name: 'Jane Foster', avatar: 'assets/images/portrait/small/avatar-s-3.jpg', email: 'jane.foster@example.com'},
        {name: 'Donna Frank', avatar: 'assets/images/portrait/small/avatar-s-1.jpg', email: 'donna.frank@example.com'},
        {
            name: 'Gabrielle Robertson',
            avatar: 'assets/images/portrait/small/avatar-s-4.jpg',
            email: 'gabrielle.robertson@example.com'
        },
        {name: 'Lori Spears', avatar: 'assets/images/portrait/small/avatar-s-6.jpg', email: 'lori.spears@example.com'},
    ];

    public emailCCSelect = [...this.emailToSelect];
    public emailBCCSelect = [...this.emailToSelect];

    public emailToSelected: any[] = [];
    public emailCCSelected: any[] = [];
    public emailBCCSelected: any[] = [];
    private modalRef!: NgbModalRef;
    public emailData = {
        to: [] as string[],
        cc: [] as string[],
        bcc: [] as string[],
        subject: '',
        body: '',
        attachments: [] as File[],
    };

    public isOpenCC = false;
    public isOpenBCC = false;
    public isComposeOpen: boolean = false;

    private _unsubscribeAll: Subject<void> = new Subject();

    constructor(
        private _emailService: EmailService,
        private toastr: ToastrService, 
        private modalService: NgbModal,
        private documentService: DocumentService,
        private clientService: ClientService,
        private fournisseurService: FournisseurService
    ) {
    }

    ngOnInit(): void {
        this._emailService.composeEmailChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(response => {
            //console.log('EmailComposeComponent: composeEmailChanged received', response);
            this.isComposeOpen = response;
            if (this.isComposeOpen) {
                setTimeout(() => {
                    if (this._selectRef && this._selectRef.searchInput) {
                        this._selectRef.searchInput.nativeElement.focus();
                    }
                }, 0);
            }
        });
    }

    togglCcBcc(toggleRef: string): void {
        if (toggleRef === 'cc') {
            this.isOpenCC = !this.isOpenCC;
        } else {
            this.isOpenBCC = !this.isOpenBCC;
        }
    }

    onFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length) {
            this.emailData.attachments = Array.from(input.files);
        }
    }

    /* sendEmail(): void {
         if (this.emailForm.valid && this.emailData.to.length > 0) {
             const emailPayload = {
                 to: this.emailData.to.map((recipient: any) => recipient.email || recipient),
                 cc: this.emailData.cc.map((recipient: any) => recipient.email || recipient),
                 bcc: this.emailData.bcc.map((recipient: any) => recipient.email || recipient),
                 subject: this.emailData.subject,
                 body: this.emailData.body,
                 attachments: this.emailData.attachments,
             };
             this._emailService.sendEmail(emailPayload).subscribe({
                 next: () => {
                     this.toastr.success('Email sent successfully', 'Success');
                     this.closeCompose();
                 },
                 error: (err) => {
                     console.error('Error sending email:', err);
                     this.toastr.error('Failed to send email', 'Error');
                 },
             });
         } else {
             this.toastr.error('Please fill in all required fields', 'Error');
         }
     }*/

    closeCompose(): void {
        this.isComposeOpen = false;
        this.emailData = {to: [], cc: [], bcc: [], subject: '', body: '', attachments: []};
        this.emailToSelected = [];
        this.emailCCSelected = [];
        this.emailBCCSelected = [];
        this.isOpenCC = false;
        this.isOpenBCC = false;
        this._emailService.composeEmail(false);
        if (this.emailForm) {
            this.emailForm.resetForm();
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    openModal() {
        const options: NgbModalOptions = {
            centered: true,
            backdrop: 'static',
            keyboard: true,
            size: 'lg',
        };
        this.modalRef = this.modalService.open(this.modalTemplate, options);
    }
}
